import { httpsCallable, getFunctions } from 'firebase/functions';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { app, auth, db } from '../firebase';
import { User } from '../types';
import { planStatusOf } from '../utils/hierarchy';

import { todayLocal } from '../utils/dates';
/** "2026-09-29" -> "29 Sep 2026", for a message a person reads. */
function formatDayMonthYear(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const functions = getFunctions(app);

/** Bootstrap administrator. Signs in with a real e-mail address. */
export const ROOT_ADMIN_EMAIL = 'ashish.kharad2@gmail.com';

/** Hidden auth domain used for every ID-based staff account. */
const INTERNAL_DOMAIN = 'scalesupport.internal';

/**
 * Normalise a Login ID to its canonical form.
 * MUST stay identical to normaliseLoginId() in functions/index.js
 */
export function normaliseLoginId(raw: string): string {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Derive the hidden Firebase Auth e-mail for a Login ID.
 *
 * This is the whole point of the ID-based scheme: the client can work out
 * the sign-in address on its own, so logging in needs ZERO Firestore reads
 * before authentication. That removes the "Missing or insufficient
 * permissions" error that used to fire on the login screen.
 *
 * MUST stay identical to buildInternalEmail() in functions/index.js
 */
export function buildInternalEmail(loginId: string): string {
  return `${normaliseLoginId(loginId)}@${INTERNAL_DOMAIN}`;
}

/** Result of a profile read: found, genuinely absent, or unreadable. */
export type ProfileReadResult =
  | { ok: true; user: User | null }
  | { ok: false; error: string };

function friendlyAuthError(code: string): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect User ID or password.';
    case 'auth/user-disabled':
      return 'This account has been deactivated. Contact your administrator.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait a minute and try again.';
    case 'auth/network-request-failed':
      return 'Network problem. Check your connection and try again.';
    default:
      return '';
  }
}

export class FirebaseAuthService {
  public onAuthStateChanged(
    callback: (user: FirebaseUser | null) => void
  ): () => void {
    return onAuthStateChanged(auth, callback);
  }

  /**
   * Sign in with either a Login ID (RA-0001) or the root admin e-mail.
   * No Firestore document is read before sign-in succeeds.
   */
  public async login(
    identifier: string,
    password: string
  ): Promise<{ success: boolean; user?: User; error?: string }> {
    const raw = (identifier || '').trim();
    const pass = (password || '').trim();

    if (!raw) return { success: false, error: 'Please enter your User ID.' };
    if (!pass) return { success: false, error: 'Please enter your password.' };

    // Candidate sign-in addresses, tried in order.
    const candidates: string[] = [];
    if (raw.includes('@')) {
      // A real e-mail (root admin, or a legacy account created before the
      // ID-based scheme). Try it as typed first, then fall back to treating
      // the local part as a Login ID so legacy rows still work.
      candidates.push(raw.toLowerCase());
      candidates.push(buildInternalEmail(raw.split('@')[0]));
    } else {
      candidates.push(buildInternalEmail(raw));
    }

    let lastError = '';
    let fbUser: FirebaseUser | null = null;

    for (const email of candidates) {
      try {
        const result = await signInWithEmailAndPassword(auth, email, pass);
        fbUser = result.user;
        break;
      } catch (err: any) {
        lastError = friendlyAuthError(err?.code) || err?.message || 'Login failed';
        // A wrong password on the first candidate should not silently try
        // the second one with the same password -- but an unknown account
        // should, so keep looping on not-found style codes only.
        if (
          err?.code !== 'auth/invalid-credential' &&
          err?.code !== 'auth/user-not-found'
        ) {
          break;
        }
      }
    }

    if (!fbUser) {
      return { success: false, error: lastError || 'Incorrect User ID or password.' };
    }

    // Make sure the ID token carries fresh custom claims before the first
    // Firestore read, otherwise security rules see a stale role.
    try {
      await fbUser.getIdToken(true);
    } catch {
      /* non-fatal */
    }

    const profileRes = await this.fetchUserProfileSafe(fbUser.uid);

    if (!profileRes.ok) {
      // The database could not be reached. Do NOT sign the user out and do
      // NOT claim their profile is missing -- that is a different problem.
      await this.logout();
      return {
        success: false,
        error:
          'Signed in, but your profile could not be loaded. Check your connection and try again.',
      };
    }

    const profile = profileRes.user;

    if (profile) {
      if (
        profile.active === false ||
        (profile as any).isDeleted ||
        (profile as any).status === 'DELETED'
      ) {
        await this.logout();
        return {
          success: false,
          error: 'This account has been deactivated. Contact your administrator.',
        };
      }

      /**
       * AN EXPIRED PLAN IS A CLOSED DOOR.
       *
       * A demo or subscription end date in the past used only to colour a
       * badge on the administrator's screen. The agent carried on working as
       * though nothing had happened, which makes the end date decoration
       * rather than a term.
       *
       * The check is deliberately here, AFTER the password has been accepted
       * and the profile read: the person is told plainly that their plan has
       * ended and on what date, rather than being shown "wrong password",
       * which would send them to reset a password that was never the problem.
       * They are then signed out again, so nothing is left half-open.
       *
       * Administrators are never locked out by this -- somebody has to be able
       * to extend the date, and it must not be possible to lock the whole
       * system by letting one plan lapse.
       */
      const plan = planStatusOf(profile);
      if (plan.status === 'expired' && profile.role !== 'admin') {
        await this.logout();
        const ended = plan.endDate ? ` It ended on ${formatDayMonthYear(plan.endDate)}.` : '';
        return {
          success: false,
          error:
            `Your ${
              profile.agentPlan === 'demo' ? 'demo period' : 'subscription'
            } has ended, so you cannot sign in.${ended} ` +
            'Ask your administrator to extend it.',
        };
      }

      return { success: true, user: profile };
    }

    // No Firestore profile yet. This only legitimately happens for the root
    // admin on a brand-new project, so build and persist one for them.
    const isRootAdmin =
      (fbUser.email || '').toLowerCase() === ROOT_ADMIN_EMAIL;

    if (!isRootAdmin) {
      await this.logout();
      return {
        success: false,
        error:
          'Your profile is missing from the database. Ask an administrator to re-create your account.',
      };
    }

    const fallback: User = {
      id: fbUser.uid,
      username: 'admin',
      name: fbUser.displayName || 'Ashish Kharad',
      email: fbUser.email || ROOT_ADMIN_EMAIL,
      role: 'admin',
      branch: 'Head Office',
      area: 'Central',
      zone: 'Central',
      agentId: 'ADM-001',
      mobile: '',
      joiningDate: todayLocal(),
      tokenVersion: 1,
      active: true,
      status: 'ACTIVE',
    } as User;

    try {
      // Own document -- allowed by the isOwner() rule.
      await setDoc(doc(db, 'users', fbUser.uid), { ...fallback, loginId: 'admin' }, { merge: true });
    } catch (saveErr) {
      console.warn('[Auth] Could not persist bootstrap admin profile:', saveErr);
    }

    return { success: true, user: fallback };
  }

  public async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (err: any) {
      console.error('[Auth] Logout failed:', err?.message);
    }
  }

  /**
   * Read a user's profile document.
   *
   * Returns null ONLY when the document genuinely does not exist.
   * A failed read THROWS instead of returning null, so callers can never
   * confuse "I could not read this" with "this user has no profile".
   * That confusion is what signed people out on every page refresh.
   */
  public async fetchUserProfile(uid: string): Promise<User | null> {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      return { ...(snap.data() as any), id: uid } as User;
    }
    return null;
  }

  /**
   * Same read, but retries a few times before giving up, and reports the
   * difference between "no such user" and "could not reach the database".
   */
  public async fetchUserProfileSafe(
    uid: string,
    attempts: number = 3
  ): Promise<ProfileReadResult> {
    let lastError = 'Could not read your profile.';

    for (let i = 0; i < attempts; i++) {
      try {
        const user = await this.fetchUserProfile(uid);
        return { ok: true, user };
      } catch (err: any) {
        lastError = err?.message || lastError;
        console.warn(
          `[Auth] Profile read attempt ${i + 1}/${attempts} failed:`,
          lastError
        );
        // Short backoff -- the token or the connection may still be settling.
        await new Promise((r) => setTimeout(r, 400 * (i + 1)));
      }
    }

    return { ok: false, error: lastError };
  }

  public subscribeUserProfile(
    uid: string,
    onUpdate: (user: User | null) => void,
    onDeactivated: () => void
  ): () => void {
    return onSnapshot(
      doc(db, 'users', uid),
      (snapshot) => {
        if (!snapshot.exists()) {
          onUpdate(null);
          return;
        }
        const userData = { ...(snapshot.data() as any), id: uid } as User;
        if (
          userData.active === false ||
          (userData as any).isDeleted ||
          (userData as any).status === 'DELETED'
        ) {
          onDeactivated();
          return;
        }
        onUpdate(userData);
      },
      (err) => {
        console.warn('[Auth] subscribeUserProfile notice:', err.message);
      }
    );
  }

  // ------------------------------------------------------------------
  // ADMIN OPERATIONS (all run server-side through Cloud Functions)
  // ------------------------------------------------------------------

  /**
   * Create a staff account. `loginId` is the staff code the person will
   * type to sign in (RA-0001). It is NEVER an e-mail address.
   */
  public async createStaffUser(
    loginId: string,
    password: string,
    userData: Partial<User> & { contactEmail?: string }
  ): Promise<{ success: boolean; uid?: string; loginId?: string; error?: string }> {
    try {
      const callable = httpsCallable(functions, 'createUser');
      const result: any = await callable({
        userId: loginId,
        password,
        name: userData.name || '',
        agencyName: (userData as any).agencyName || userData.name || '',
        role: userData.role || 'agent',
        branch: userData.branch || '',
        area: userData.area || '',
        zone: userData.zone || '',
        zonalOffice: (userData as any).zonalOffice || userData.zone || '',
        regionalOffice: (userData as any).regionalOffice || '',
        agentId: userData.agentId || loginId,
        mobile: userData.mobile || '',
        contactEmail: userData.contactEmail || userData.email || '',
        bank: (userData as any).bank || '',
        bankId: (userData as any).bankId || '',
        department: (userData as any).department || '',
        monthlyTarget: (userData as any).monthlyTarget ?? null,
      });
      return {
        success: true,
        uid: result.data.uid,
        loginId: result.data.loginId,
      };
    } catch (err: any) {
      console.error('[Auth] createStaffUser failed:', err?.message);
      return { success: false, error: err?.message || 'User creation failed' };
    }
  }

  /** Backwards-compatible alias used by older call sites. */
  public async createAuthUserWithoutSwitchingSession(
    loginId: string,
    pass: string,
    userData: Partial<User>
  ): Promise<{ success: boolean; uid?: string; loginId?: string; error?: string }> {
    return this.createStaffUser(loginId, pass, userData);
  }

  /**
   * Admin sets a new password directly. No e-mail, no reset link.
   */
  public async adminResetPassword(
    uidOrLoginId: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const callable = httpsCallable(functions, 'resetUserPassword');
      await callable({ uid: uidOrLoginId, newPassword });
      return { success: true };
    } catch (err: any) {
      console.error('[Auth] adminResetPassword failed:', err?.message);
      return { success: false, error: err?.message || 'Password reset failed' };
    }
  }

  public async adminSetUserActive(
    uidOrLoginId: string,
    active: boolean
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const callable = httpsCallable(functions, 'setUserActive');
      await callable({ uid: uidOrLoginId, active });
      return { success: true };
    } catch (err: any) {
      console.error('[Auth] adminSetUserActive failed:', err?.message);
      return { success: false, error: err?.message || 'Update failed' };
    }
  }

  public async adminDeleteUser(
    uidOrLoginId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const callable = httpsCallable(functions, 'deleteUserAccount');
      await callable({ uid: uidOrLoginId });
      return { success: true };
    } catch (err: any) {
      console.error('[Auth] adminDeleteUser failed:', err?.message);
      return { success: false, error: err?.message || 'Delete failed' };
    }
  }

  public async adminUpdateLoginId(
    uid: string,
    newLoginId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const callable = httpsCallable(functions, 'updateLoginId');
      await callable({ uid, newLoginId });
      return { success: true };
    } catch (err: any) {
      console.error('[Auth] adminUpdateLoginId failed:', err?.message);
      return { success: false, error: err?.message || 'Update failed' };
    }
  }

  /**
   * Gmail reset link. Only meaningful for the root admin, who is the one
   * account with a real mailbox. Staff passwords are reset by the admin
   * through adminResetPassword().
   */
  public async sendPasswordReset(
    email: string
  ): Promise<{ success: boolean; error?: string; message?: string }> {
    const target = (email || '').trim().toLowerCase();
    if (!target || !target.includes('@') || target.endsWith(INTERNAL_DOMAIN)) {
      return {
        success: false,
        error:
          'This account has no mailbox. Ask the administrator to set a new password for you.',
      };
    }
    try {
      await sendPasswordResetEmail(auth, target);
      return { success: true, message: `Reset link sent to ${target}.` };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Password reset failed' };
    }
  }
}

export const firebaseAuthService = new FirebaseAuthService();
